<script lang="ts" setup>
import type { AsyncDataRequestStatus } from '#app'

import AboutSection from '~/components/sections/AboutSection.vue'
import ArticlesSection from '~/components/sections/ArticlesSection.vue'
import BannersSection from '~/components/sections/BannersSection.vue'
import BrandsSection from '~/components/sections/BrandsSection.vue'
import CollectionsSection from '~/components/sections/CollectionsSection.vue'
import ConsultationsSection from '~/components/sections/ConsultationsSection.vue'
import CountdownSection from '~/components/sections/CountdownSection.vue'
import MindboxRecommendationsSection from '~/components/sections/MindboxRecommendationsSection.vue'
import PartnerSection from '~/components/sections/PartnerSection.vue'
import ProductsSection from '~/components/sections/ProductsSection.vue'
import SpecialOffersSection from '~/components/sections/SpecialOffersSection.vue'
import SubscriptionSection from '~/components/sections/SubscriptionSection.vue'
import { useAction } from '~/composables/action'
import { useSharing } from '~/composables/sharing'
import { useWindowHelpers } from '~/composables/window-helpers'
import constants from '~/constants'
import type Action from '~/enums/action'
import { MindboxOperation } from '~/enums/mindbox-operation'
import Page from '~/enums/page'
import ProductSort from '~/enums/product-sort'
import JsonApiHelper from '~/helpers/json-api-helper'
import { useUserStore } from '~/store/user'
import type { BannerData } from '~/types/banners'
import type { BrandData } from '~/types/brands'
import type { ProductData } from '~/types/products'

const title = 'iTAB — платформа здорового образа жизни'

const { $apiHelper } = useNuxtApp()
const route = useRoute()

const { performActionIfNeeded } = useAction()
const { parsePromoCodeFromQuery } = useSharing()
const { scrollToSavedPosition } = useWindowHelpers()

const userStore = useUserStore()

const { Newest } = ProductSort
const params = { 'page[size]': 12 }
const paramsForProducts = { ...params, sort: Newest }

const { data, status } = await useLazyAsyncData(
  'index',
  async () => {
    const [
      articlesResponse, bannersResponse, brandsResponse, productsResponse1, productsResponse2
    ] = await Promise.all([
      $apiHelper.articles.getArticles(params),
      $apiHelper.banners.getBanners({ 'filter[platform]': 'website' }),
      $apiHelper.brands.getBrands({ 'filter[hasImages]': 'yes', include: 'images', 'page[size]': 24 }),
      $apiHelper.products.getProducts({
        ...paramsForProducts,
        'filter[categoryId]': constants.categories.thisWeekDiscounts.id
      }),
      $apiHelper.products.getProducts(paramsForProducts)
    ])

    return { articlesResponse, bannersResponse, brandsResponse, productsResponse1, productsResponse2 }
  },
  {
    transform: (x) => ({
      articlesResponse: JsonApiHelper.denormalizeResponse(x?.articlesResponse),
      bannersResponse: JsonApiHelper.denormalizeResponse(x?.bannersResponse),
      brandsResponse: JsonApiHelper.denormalizeResponse(x?.brandsResponse),
      productsResponse1: JsonApiHelper.denormalizeResponse(x?.productsResponse1),
      productsResponse2: JsonApiHelper.denormalizeResponse(x?.productsResponse2)
    })
  }
)

useHead({
  title,
  meta: [{
    name: 'description',
    content: 'iTAB — витамины, БАД и экотовары от проверенных брендов. Качественные товары для здоровья и красоты. Бесплатная доставка по России и СНГ.'
  }]
})

const articlesData = computed<any>(() => data.value?.articlesResponse?.data ?? [])
const bannersData = computed<BannerData[]>(() => data.value?.bannersResponse?.data ?? [])
const brandsData = computed<BrandData[]>(() => data.value?.brandsResponse?.data ?? [])
const productsData1 = computed<ProductData[]>(() => data.value?.productsResponse1?.data ?? [])
const productsData2 = computed<ProductData[]>(() => data.value?.productsResponse2?.data ?? [])

watch(
  status,
  (x: AsyncDataRequestStatus): void => {
    if (x === 'success') {
      scrollToSavedPosition()
    }
  }
)

onMounted(async (): Promise<void> => {
  const query = { ...route.query }
  const { action } = query

  await performActionIfNeeded(action as Action, query) // For backward compatibility.

  await parsePromoCodeFromQuery()
})
</script>

<template>
  <div>
    <section>
      <div class="container">
        <h1 class="visually-hidden">
          {{ title }}
        </h1>
      </div>
    </section>

    <CountdownSection class="-mt-4 md:mb-[60px] md:mt-[-40px]" event="green-friday" is-active />

    <BannersSection :banners-data="bannersData" :is-data-loading="status === 'pending'" />

    <Marquee v-if="userStore.isSpecialist" class="mt-10 bg-white lg:mt-[60px]">
      <NuxtLink
        v-for="i in 10"
        :key="i"
        class="mr-[280px] flex max-w-[280px] !cursor-pointer items-center py-2 text-main-green !no-underline"
        to="/products/biomins-vitamin-e-kompleks-tokoferolov-i-tokotrienolov-kapsuly-60-sht-3000/"
      >
        <b class="inline-block text-lg font-medium leading-normal tracking-[-0.01em] md:text-xl">
          Кешбэк&nbsp;15% до&nbsp;конца месяца на&nbsp;витамин&nbsp;E от&nbsp;Biomins
        </b>
      </NuxtLink>
    </Marquee>

    <MindboxRecommendationsSection
      class="mt-10 lg:mt-[60px]"
      :data="{ recommendation: { limit: 12 } }"
      :operation="MindboxOperation.PersonalRecommendations"
      title="Специально для вас"
    />

    <MindboxRecommendationsSection
      class="mt-10 lg:mt-[60px]"
      :data="{ recommendation: { limit: 12 } }"
      :operation="MindboxOperation.PopularProducts"
      :path="Page.Search"
      :query-parameters="{ sort: ProductSort.MostPopular }"
      title="Популярное"
    />

    <Preloader v-if="status === 'pending'" />
    <template v-else-if="status === 'success'">
      <CollectionsSection class="mt-10 lg:mt-[60px]" :collections-data="[1]" :title="'Подборки от\xA0экспертов'" />

      <ConsultationsSection class="mt-10 lg:mt-[60px]" />

      <PartnerSection class="mt-10 lg:mt-[60px]" />

      <ProductsSection
        class="mt-10 lg:mt-[60px]"
        :path="constants.categories.thisWeekDiscounts.path"
        :products-data="productsData1"
        :title="constants.categories.thisWeekDiscounts.name"
      />

      <ProductsSection
        class="mt-10 lg:mt-[60px]"
        :path="Page.Search"
        :products-data="productsData2"
        :query-parameters="{ sort: 'param.Дата добавления' }"
        title="Новинки"
      />

      <SpecialOffersSection class="mt-10 lg:mt-[60px]" />
    </template>

    <AboutSection class="mt-10 lg:mt-[60px]" />

    <Preloader v-if="status === 'pending'" />
    <template v-else-if="status === 'success'">
      <BrandsSection :brands-data="brandsData" class="mt-10 lg:mt-[60px]" title="Наши бренды" />

      <ArticlesSection :articles-data="articlesData" class="mt-10 lg:mt-[60px]" :title="'Статьи и\xA0материалы'" />
    </template>

    <SubscriptionSection class="mt-10 lg:mt-[60px]" />

    <MindboxRecommendationsSection
      class="mt-10 lg:mt-[60px]"
      :data="{ recommendation: { limit: 12 } }"
      :operation="MindboxOperation.RecentlyViewedProducts"
      :title="'Ранее вы\xA0смотрели'"
    />
  </div>
</template>
